import React, { useState, useEffect } from 'react';
import { Box, TextField, List, ListItem, ListItemAvatar, Avatar, 
         ListItemText, Typography, Divider, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useChat } from './ChatContext';
import axios from 'axios';
import Cookies from 'js-cookie';
import styled from 'styled-components';

// Styled components for the control panel
const StyledSearchField = styled(TextField)`
  margin-bottom: 12px;
  .MuiOutlinedInput-root {
    background-color: #363636;
    border-radius: 8px;
    
    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: #4a9bfc;
    }
    
    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: #4a9bfc;
    }
  }
  
  .MuiOutlinedInput-input {
    color: #e0e0e0;
  }
  
  .MuiInputLabel-root {
    color: #9e9e9e;
    
    &.Mui-focused {
      color: #4a9bfc;
    }
  }
`;

const UserListContainer = styled(Box)`
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 16px;
  
  /* Custom Scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #252526;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.25);
  }
`;

/**
 * ChatControl component to manage users and create new chats
 * @param {function} onChatCreated - Callback when a new chat is created
 * @param {function} onClose - Callback to close the control panel
 */
const ChatControl = ({ onChatCreated, onClose }) => {
  const { startPrivateChat, switchChat, privateChats } = useChat();
  const [users, setUsers] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);

  // Fetch all users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = Cookies.get('token');
      const response = await axios.get(
        `${process.env.REACT_APP_STATUS_API_URL}/admin/list_users`, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data && response.data.users) {
        setUsers(response.data.users);
        filterAndSortUsers(response.data.users, onlineUsers, searchQuery);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch online users
  const fetchOnlineUsers = async () => {
    try {
      const token = Cookies.get('token');
      const response = await axios.get(
        `${process.env.REACT_APP_STATUS_API_URL}/user/chat/online-users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data && response.data.users) {
        setOnlineUsers(response.data.users);
        filterAndSortUsers(users, response.data.users, searchQuery);
      }
    } catch (error) {
      console.error('Failed to fetch online users:', error);
    }
  };

  // Filter and sort users based on online status and search query
  const filterAndSortUsers = (allUsers, online, query) => {
    // Create a map of online usernames for fast lookup
    const onlineUserMap = new Map();
    online.forEach(user => onlineUserMap.set(user.username, true));
    
    // Filter by search query
    let filtered = allUsers;
    if (query) {
      const lowercaseQuery = query.toLowerCase();
      filtered = allUsers.filter(user => 
        user.username.toLowerCase().includes(lowercaseQuery) ||
        user.fornavn.toLowerCase().includes(lowercaseQuery) ||
        user.etternavn.toLowerCase().includes(lowercaseQuery)
      );
    }
    
    // Mark users as online or offline
    filtered = filtered.map(user => ({
      ...user,
      isOnline: onlineUserMap.has(user.username)
    }));
    
    // Sort online users first, then alphabetically by name
    filtered.sort((a, b) => {
      if (a.isOnline !== b.isOnline) {
        return a.isOnline ? -1 : 1; // Online users first
      }
      // If online status is the same, sort alphabetically
      return `${a.fornavn} ${a.etternavn}`.localeCompare(`${b.fornavn} ${b.etternavn}`);
    });
    
    setFilteredUsers(filtered);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    filterAndSortUsers(users, onlineUsers, query);
  };

  // Handle click on a user to start a chat
  const handleUserClick = async (user) => {
    try {
      setLoading(true);
      
      // Check if chat already exists
      const existingChat = privateChats.find(chat => 
        chat.participants && 
        Array.isArray(chat.participants) && 
        chat.participants.includes(user.username)
      );
      
      if (existingChat) {
        console.log("Found existing chat:", existingChat);
        switchChat(existingChat);
        if (onChatCreated) onChatCreated(existingChat);
        if (onClose) onClose();
      } else {
        // Start a new private chat
        console.log("Creating new private chat with:", user.username);
        const newChat = await startPrivateChat(user.username); // Dette kallet bør gå til /chat/start-private endepunktet
        
        if (newChat) {
          switchChat(newChat);
          if (onChatCreated) onChatCreated(newChat);
          if (onClose) onClose();
        } else {
          console.error("Failed to create new chat");
        }
      }
    } catch (error) {
      console.error('Error handling user click:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load users and online status when component mounts
  useEffect(() => {
    fetchUsers();
    fetchOnlineUsers();
    
    // Set up periodic refresh of online status
    const intervalId = setInterval(fetchOnlineUsers, 30000);
    
    return () => clearInterval(intervalId);
  }, []);
  
  // Update filtered users when search query changes
  useEffect(() => {
    filterAndSortUsers(users, onlineUsers, searchQuery);
  }, [searchQuery, users, onlineUsers]);

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" sx={{ mb: 2, color: '#e0e0e0' }}>
        New Conversation
      </Typography>
      
      <StyledSearchField
        fullWidth
        variant="outlined"
        size="small"
        placeholder="Search users..."
        value={searchQuery}
        onChange={handleSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#9e9e9e' }} />
            </InputAdornment>
          ),
        }}
      />
      
      <UserListContainer sx={{ flex: 1 }}>
        <List disablePadding>
          {filteredUsers.map((user) => (
            <React.Fragment key={user.username}>
              <ListItem 
                button 
                onClick={() => handleUserClick(user)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
                }}
              >
                <ListItemAvatar>
                  <Box position="relative" sx={{ display: 'inline-block' }}>
                    <Avatar>
                      {user.fornavn[0]}{user.etternavn[0]}
                    </Avatar>
                    <Box 
                      component={FiberManualRecordIcon}
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        color: user.isOnline ? '#4caf50' : '#666',
                        fontSize: 12,
                        backgroundColor: '#252526',
                        borderRadius: '50%',
                      }}
                    />
                  </Box>
                </ListItemAvatar>
                <ListItemText 
                  primary={`${user.fornavn} ${user.etternavn}`}
                  secondary={
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {user.username} • {user.isOnline ? 'Online' : 'Offline'}
                    </Typography>
                  }
                  primaryTypographyProps={{
                    noWrap: true,
                    color: '#e0e0e0',
                  }}
                />
              </ListItem>
              <Divider component="li" sx={{ my: 0.5 }} />
            </React.Fragment>
          ))}
          
          {filteredUsers.length === 0 && !loading && (
            <Typography 
              variant="body2" 
              sx={{ color: '#9e9e9e', py: 2, textAlign: 'center' }}
            >
              No users found. Try a different search term.
            </Typography>
          )}
          
          {loading && (
            <Typography 
              variant="body2" 
              sx={{ color: '#9e9e9e', py: 2, textAlign: 'center' }}
            >
              Loading users...
            </Typography>
          )}
        </List>
      </UserListContainer>
      
      <Typography variant="caption" sx={{ color: '#9e9e9e', display: 'block', textAlign: 'center', mt: 2 }}>
        Click on a user to start a conversation  {/* Update instruction text */}
      </Typography>
    </Box>
  );
};

export default ChatControl;
